// Bancos de dados: visões de board, tabela, lista e calendário, com filtros,
// ordenação, criação no lugar, arrastar entre colunas e edição de células.

import * as store from "./store.js";
import * as P from "./props.js";
import { el, menu, popup, toast, titleOf, debounce, COLORS } from "./dom.js";
import { iconNode } from "./icons.js";

const LAYOUTS = [["board", "Board", "▥"], ["table", "Tabela", "☷"], ["list", "Lista", "☰"], ["calendar", "Calendário", "📅"]];
const OPS = {
  is: "é", is_not: "não é", contains: "contém", empty: "está vazio", not_empty: "não está vazio",
};
const localView = new Map(); // dbId -> id da visão escolhida (por pessoa, no aparelho)

// ctx: {canEdit, openRow(id), openSettings(tab)}
export function mountDatabase(host, dbId, ctx) {
  let data = store.cached(dbId);
  let query = "";
  let destroyed = false;
  let scheduled = false;
  let inlineGroup = undefined; // grupo onde está aberta a criação no lugar
  const root = el("div", { class: "db" });
  host.replaceChildren(root);

  const schema = () => data.page.schema || { properties: [], views: [] };
  const props = () => schema().properties || [];
  const prop = (id) => props().find((p) => p.id === id);
  const views = () => (schema().views?.length ? schema().views : [{ id: "table", name: "Tabela", type: "table" }]);
  const view = () => views().find((v) => v.id === localView.get(dbId)) || views()[0];
  const users = () => data.users || store.state.users;
  const propCtx = () => ({
    users: users(), canEdit: ctx.canEdit, canEditSchema: ctx.canEdit, openRow: ctx.openRow,
    saveSchema: () => saveSchema(), setProp: (row, p, v) => setProp(row, p, v),
  });

  const schedule = () => {
    if (scheduled || destroyed) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      if (!destroyed) render();
    });
  };

  // Recarrega do servidor (propriedades calculadas, mudanças de outras pessoas).
  const refetch = debounce(async () => {
    try {
      data = await store.load(dbId, { fresh: true });
      schedule();
    } catch { /* tenta no próximo ciclo */ }
  }, 500);

  const offs = [
    store.on("rows", (id) => { if (id === dbId) { data = store.cached(dbId) || data; schedule(); } }),
    store.on("rows-stale", (id) => {
      const related = id === dbId || props().some((p) => p.type === "relation" && p.database === id);
      if (related) refetch();
    }),
    store.on("page", (p) => { if (p.id === dbId) { data = store.cached(dbId) || data; schedule(); } }),
  ];

  async function saveSchema(next = schema()) {
    data.page.schema = next;
    schedule();
    if (!ctx.canEdit) return;
    await store.patch(dbId, { schema: next }).catch(() => {});
  }

  function setProp(row, p, value) {
    const changes = p.id === "title" ? { title: value || "" } : { props: { [p.id]: value } };
    store.setRowLocal(dbId, row.id, changes);
    store.patch(row.id, p.id === "title" ? { title: value || "" } : { props: { [p.id]: value } }).catch(() => refetch());
  }

  function newRow(propsInit = {}, title = "", { open = false } = {}) {
    const { id } = store.create({ parent_id: dbId, title, props: propsInit });
    if (open) ctx.openRow(id);
    return id;
  }

  // ---------- Linhas visíveis ----------

  function normFilters(v) {
    if (!v.filter) return [];
    return Array.isArray(v.filter) ? v.filter : [v.filter];
  }

  function matches(row, f) {
    const p = f.prop === "title" ? { id: "title", type: "text" } : prop(f.prop);
    if (!p) return true;
    const v = P.valueOf(p, row);
    const list = [].concat(v ?? []).map((x) => String(x));
    switch (f.op) {
      case "is": return p.type === "checkbox" ? !!v === (f.value === true || f.value === "true") : list.includes(String(f.value));
      case "is_not": return p.type === "checkbox" ? !!v !== (f.value === true || f.value === "true") : !list.includes(String(f.value));
      case "contains": return list.join(" ").toLowerCase().includes(String(f.value || "").toLowerCase());
      case "empty": return P.isEmpty(v);
      case "not_empty": return !P.isEmpty(v);
      default: return true;
    }
  }

  function visibleRows(v) {
    let rows = (data.rows || []).filter((r) => normFilters(v).every((f) => matches(r, f)));
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter((r) => (r.title || "").toLowerCase().includes(q));
    }
    const sorts = v.sort ? [].concat(v.sort) : [];
    rows = [...rows].sort((a, b) => {
      for (const s of sorts) {
        const p = s.prop === "title" ? { id: "title", type: "text" } : prop(s.prop);
        if (!p) continue;
        const c = compare(sortKey(p, a), sortKey(p, b));
        if (c) return s.dir === "desc" ? -c : c;
      }
      return a.position - b.position;
    });
    return rows;
  }

  function sortKey(p, row) {
    const v = P.valueOf(p, row);
    if (p.type === "select" || p.type === "status") {
      const i = (p.options || []).findIndex((o) => o.name === v);
      return i < 0 ? 9999 : i;
    }
    if (Array.isArray(v)) return v.join(",");
    return v ?? "";
  }

  const compare = (a, b) => (a === b ? 0 : a === "" ? 1 : b === "" ? -1 : typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b), "pt-BR", { numeric: true }));

  function shownProps(v, layout) {
    if (Array.isArray(v.visible)) return v.visible.map(prop).filter(Boolean);
    const all = props();
    if (layout === "table") return all.filter((p) => !["text"].includes(p.type) || all.length <= 6).slice(0, 8);
    return all.filter((p) => ["select", "status", "person", "date", "multi_select", "id"].includes(p.type) && p.id !== v.groupBy).slice(0, 4);
  }

  // ---------- Desenho ----------

  function render() {
    if (!data) return;
    // Criando linhas no lugar: atualiza só as linhas, sem perder o foco nem o que está sendo digitado.
    if (root.querySelector(".inline-new") && root.contains(document.activeElement) && document.activeElement.classList.contains("inline-new")) {
      return softRender();
    }
    const v = view();
    const scrollers = [...root.querySelectorAll(".board,.table-scroll")].map((s) => [s.className, s.scrollLeft, s.scrollTop]);
    root.replaceChildren(toolbar(v), filterBar(v), body(v));
    for (const [cls, left, top] of scrollers) {
      const s = root.querySelector("." + cls.split(" ")[0]);
      if (s) { s.scrollLeft = left; s.scrollTop = top; }
    }
  }

  function softRender() {
    const v = view();
    const rows = visibleRows(v);
    if (v.type === "board") {
      const gp = prop(v.groupBy);
      const shown = shownProps(v, "board");
      for (const col of root.querySelectorAll(".board-col")) {
        const value = col.dataset.value || null;
        const items = rows.filter((r) => (r.props[gp.id] ?? null) === value);
        col.querySelector(".board-cards").replaceChildren(...items.map((r) => card(r, shown, gp)));
        col.querySelector(".count").textContent = items.length;
      }
    } else if (v.type === "table") {
      const fresh = table(v, rows).querySelector(".dtable");
      root.querySelector(".dtable")?.replaceWith(fresh);
    } else if (v.type === "list") {
      const box = root.querySelector(".dlist");
      const fresh = list(v, rows);
      for (const r of box.querySelectorAll(".dlist-row")) r.remove();
      const anchor = box.querySelector(".card.new");
      for (const r of fresh.querySelectorAll(".dlist-row")) box.insertBefore(r, anchor);
    }
  }

  function body(v) {
    const rows = visibleRows(v);
    if (v.type === "board") return board(v, rows);
    if (v.type === "calendar") return calendar(v, rows);
    if (v.type === "list") return list(v, rows);
    return table(v, rows);
  }

  function toolbar(v) {
    const search = el("input", { class: "db-search", placeholder: "Buscar…", value: query });
    search.addEventListener("input", debounce(() => { query = search.value.trim(); renderBodyOnly(); }, 120));
    const filters = normFilters(v);
    return el("div", { class: "db-bar" },
      el("div", { class: "db-tabs" },
        views().map((x) => el("button", {
          class: "db-tab" + (x.id === v.id ? " active" : ""),
          onclick: (e) => {
            if (x.id === v.id && ctx.canEdit) return viewMenu(e.currentTarget, x);
            localView.set(dbId, x.id);
            render();
          },
          oncontextmenu: (e) => { e.preventDefault(); if (ctx.canEdit) viewMenu(e.currentTarget, x); },
          title: x.id === v.id && ctx.canEdit ? "Clique para editar a visão" : "",
        }, el("span", { class: "db-tab-icon" }, (LAYOUTS.find((l) => l[0] === x.type) || LAYOUTS[1])[2]), x.name)),
        ctx.canEdit ? el("button", { class: "icon-btn", title: "Nova visão", onclick: (e) => addView(e.currentTarget) }, "+") : null),
      el("div", { class: "db-tools" },
        search,
        el("button", { class: "tool" + (filters.length ? " on" : ""), onclick: (e) => filterMenu(e.currentTarget, v) }, "Filtro"),
        el("button", { class: "tool" + (v.sort?.length ? " on" : ""), onclick: (e) => sortMenu(e.currentTarget, v) }, "Ordenar"),
        el("button", { class: "tool", onclick: (e) => propsMenu(e.currentTarget, v) }, "Propriedades"),
        ctx.canEdit ? el("button", { class: "tool", title: "Propriedades, relações e automações", onclick: () => ctx.openSettings() }, "⚙") : null,
        ctx.canEdit ? el("button", { class: "primary small", onclick: () => newRow(defaultsFor(v), "", { open: true }) }, "Nova") : null));
  }

  function renderBodyOnly() {
    const old = root.querySelector(".db-view");
    old?.replaceWith(body(view()));
  }

  // Valores iniciais de uma linha nova: o que os filtros da visão exigem.
  function defaultsFor(v, extra = {}) {
    const out = {};
    for (const f of normFilters(v)) {
      const p = prop(f.prop);
      if (!p || f.op !== "is") continue;
      out[p.id] = p.type === "multi_select" || p.type === "person" ? [f.value] : p.type === "checkbox" ? f.value === true || f.value === "true" : f.value;
    }
    return { ...out, ...extra };
  }

  function filterBar(v) {
    const filters = normFilters(v);
    if (!filters.length) return el("div", { class: "db-filters", hidden: true });
    return el("div", { class: "db-filters" }, filters.map((f, i) => {
      const p = f.prop === "title" ? { name: "Nome" } : prop(f.prop);
      return el("span", { class: "filter-chip" },
        el("button", { class: "link", onclick: (e) => editFilter(e.currentTarget, v, i) }, `${p?.name || "?"} ${OPS[f.op] || f.op}${["empty", "not_empty"].includes(f.op) ? "" : ": " + displayFilterValue(p, f.value)}`),
        el("button", { class: "icon-btn tiny", title: "Remover filtro", onclick: () => { setFilters(v, filters.filter((_, j) => j !== i)); } }, "×"));
    }));
  }

  function displayFilterValue(p, value) {
    if (p?.type === "person") return users().find((u) => u.id === value)?.name || value;
    if (p?.type === "checkbox") return value === true || value === "true" ? "marcado" : "desmarcado";
    return value ?? "";
  }

  function setFilters(v, filters) {
    v.filter = filters;
    saveSchema();
  }

  // ---------- Menus da barra ----------

  function viewMenu(anchor, v) {
    menu(anchor, [
      { label: "Renomear visão", icon: "✎", run: () => renameView(anchor, v) },
      ...LAYOUTS.filter((l) => l[0] !== v.type).map((l) => ({ label: `Mostrar como ${l[1]}`, icon: l[2], run: () => { v.type = l[0]; ensureLayout(v); saveSchema(); } })),
      v.type === "board" && { label: "Agrupar por…", icon: "▥", run: () => groupMenu(anchor, v) },
      v.type === "calendar" && { label: "Data usada…", icon: "📅", run: () => dateMenu(anchor, v) },
      { label: "Duplicar visão", icon: "⧉", run: () => { const c = { ...structuredClone(v), id: "v" + Date.now().toString(36), name: v.name + " (cópia)" }; schema().views = [...views(), c]; localView.set(dbId, c.id); saveSchema(); } },
      views().length > 1 && "-",
      views().length > 1 && { label: "Excluir visão", icon: "🗑", danger: true, run: () => { schema().views = views().filter((x) => x.id !== v.id); saveSchema(); } },
    ]);
  }

  function renameView(anchor, v) {
    const input = el("input", { class: "pop-input", value: v.name });
    const box = popup(anchor, el("div", { class: "pop-edit" }, input), { onClose: () => { if (input.value.trim() && input.value.trim() !== v.name) { v.name = input.value.trim(); saveSchema(); } } });
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") box.close(); });
    requestAnimationFrame(() => { input.focus(); input.select(); });
  }

  function ensureLayout(v) {
    if (v.type === "board" && !prop(v.groupBy)) v.groupBy = props().find((p) => ["select", "status"].includes(p.type))?.id;
    if (v.type === "calendar" && !prop(v.dateProp)) v.dateProp = props().find((p) => p.type === "date")?.id;
  }

  function addView(anchor) {
    menu(anchor, LAYOUTS.map(([type, label, ic]) => ({
      label, icon: ic, run: () => {
        const v = { id: "v" + Date.now().toString(36), name: label, type };
        ensureLayout(v);
        schema().views = [...views(), v];
        localView.set(dbId, v.id);
        saveSchema();
      },
    })));
  }

  function groupMenu(anchor, v) {
    const opts = props().filter((p) => ["select", "status"].includes(p.type));
    if (!opts.length) return toast("Crie uma propriedade de seleção (⚙) para agrupar o board.");
    menu(anchor, opts.map((p) => ({ label: p.name, icon: p.id === v.groupBy ? "✓" : "", run: () => { v.groupBy = p.id; v.hiddenGroups = []; saveSchema(); } })));
  }

  function dateMenu(anchor, v) {
    const opts = props().filter((p) => p.type === "date");
    if (!opts.length) return toast("Crie uma propriedade de data (⚙) para usar o calendário.");
    menu(anchor, opts.map((p) => ({ label: p.name, icon: p.id === v.dateProp ? "✓" : "", run: () => { v.dateProp = p.id; saveSchema(); } })));
  }

  function filterMenu(anchor, v) {
    const all = [{ id: "title", name: "Nome", type: "text" }, ...props().filter((p) => !["relation", "rollup", "created_time", "updated_time"].includes(p.type))];
    menu(anchor, [
      ...all.map((p) => ({ label: p.name, icon: P.typeIcon(p.type), run: () => {
        const f = { prop: p.id, op: ["select", "status", "multi_select", "person", "checkbox"].includes(p.type) ? "is" : "contains", value: p.type === "checkbox" ? true : "" };
        const filters = [...normFilters(v), f];
        v.filter = filters;
        render();
        requestAnimationFrame(() => editFilter(root.querySelectorAll(".filter-chip .link")[filters.length - 1] || anchor, v, filters.length - 1));
      } })),
      normFilters(v).length ? "-" : null,
      normFilters(v).length ? { label: "Limpar filtros", icon: "×", run: () => setFilters(v, []) } : null,
    ]);
  }

  function editFilter(anchor, v, i) {
    const filters = normFilters(v);
    const f = filters[i];
    const p = f.prop === "title" ? { id: "title", name: "Nome", type: "text" } : prop(f.prop);
    if (!p) return;
    const ops = ["select", "status", "multi_select", "person"].includes(p.type) ? ["is", "is_not", "empty", "not_empty"]
      : p.type === "checkbox" ? ["is"] : ["contains", "is", "is_not", "empty", "not_empty"];
    const opSel = el("select", {}, ops.map((o) => el("option", { value: o, selected: o === f.op }, OPS[o])));
    let valueInput;
    if (["select", "status", "multi_select"].includes(p.type)) {
      valueInput = el("select", {}, el("option", { value: "" }, "—"), (p.options || []).map((o) => el("option", { value: o.name, selected: o.name === f.value }, o.name)));
    } else if (p.type === "person") {
      valueInput = el("select", {}, el("option", { value: "" }, "—"), users().map((u) => el("option", { value: u.id, selected: u.id === f.value }, u.name)));
    } else if (p.type === "checkbox") {
      valueInput = el("select", {}, el("option", { value: "true", selected: f.value === true || f.value === "true" }, "marcado"), el("option", { value: "false", selected: f.value === false || f.value === "false" }, "desmarcado"));
    } else {
      valueInput = el("input", { class: "pop-input", value: f.value || "", placeholder: "Valor" });
    }
    const apply = () => {
      f.op = opSel.value;
      f.value = p.type === "checkbox" ? valueInput.value === "true" : valueInput.value;
      v.filter = filters;
      saveSchema();
    };
    opSel.addEventListener("change", apply);
    valueInput.addEventListener("change", apply);
    valueInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { apply(); box.close(); } });
    const box = popup(anchor, el("div", { class: "pop-edit filter-edit" }, el("strong", {}, p.name), opSel, valueInput,
      el("button", { class: "link danger", onclick: () => { box.close(); setFilters(v, filters.filter((_, j) => j !== i)); } }, "Remover filtro")));
  }

  function sortMenu(anchor, v) {
    const all = [{ id: "title", name: "Nome", type: "text" }, ...props()];
    const sorts = v.sort ? [].concat(v.sort) : [];
    menu(anchor, [
      ...sorts.map((s) => ({ label: `${(all.find((p) => p.id === s.prop) || {}).name || "?"} ${s.dir === "desc" ? "↓" : "↑"} (remover)`, icon: "×", run: () => { v.sort = sorts.filter((x) => x !== s); saveSchema(); } })),
      sorts.length ? "-" : null,
      ...all.map((p) => ({ label: `${p.name} ↑`, icon: P.typeIcon(p.type), hint: "crescente", run: () => { v.sort = [...sorts.filter((x) => x.prop !== p.id), { prop: p.id, dir: "asc" }]; saveSchema(); } })),
      ...all.map((p) => ({ label: `${p.name} ↓`, icon: P.typeIcon(p.type), hint: "decrescente", run: () => { v.sort = [...sorts.filter((x) => x.prop !== p.id), { prop: p.id, dir: "desc" }]; saveSchema(); } })),
    ]);
  }

  function propsMenu(anchor, v) {
    const shown = new Set(shownProps(v, v.type).map((p) => p.id));
    const list = el("div", { class: "pick-list" });
    const draw = () => list.replaceChildren(...props().map((p) => el("label", { class: "pick-row" },
      el("input", { type: "checkbox", checked: shown.has(p.id), onchange: (e) => {
        if (e.target.checked) shown.add(p.id); else shown.delete(p.id);
        v.visible = props().filter((x) => shown.has(x.id)).map((x) => x.id);
        saveSchema();
      } }), el("span", { class: "prop-icon" }, P.typeIcon(p.type)), p.name)));
    draw();
    popup(anchor, el("div", { class: "pop-pick" }, el("div", { class: "pick-label" }, "Mostrar nesta visão"), list,
      ctx.canEdit ? el("button", { class: "link", onclick: () => ctx.openSettings() }, "Editar propriedades…") : null), { align: "right" });
  }

  // ---------- Board ----------

  function board(v, rows) {
    const gp = prop(v.groupBy);
    if (!gp) {
      return el("div", { class: "db-view db-empty" },
        el("p", {}, "Escolha uma propriedade de seleção para agrupar o board."),
        ctx.canEdit ? el("button", { onclick: (e) => groupMenu(e.currentTarget, v) }, "Agrupar por…") : null);
    }
    const hidden = new Set(v.hiddenGroups || []);
    for (const f of normFilters(v)) if (f.prop === gp.id && f.op === "is_not") hidden.add(f.value);
    const groups = [{ value: null, color: "default" }, ...(gp.options || []).map((o) => ({ value: o.name, color: o.color }))];
    const cols = [];
    const hiddenCols = [];
    for (const g of groups) {
      const items = rows.filter((r) => (r.props[gp.id] ?? null) === g.value);
      if (g.value === null && !items.length && inlineGroup !== null) continue;
      if (hidden.has(g.value ?? "")) { hiddenCols.push({ ...g, count: items.length }); continue; }
      cols.push(column(v, gp, g, items));
    }
    return el("div", { class: "db-view board" }, cols,
      hiddenCols.length ? el("div", { class: "board-hidden" }, el("div", { class: "muted small" }, "Grupos ocultos"),
        hiddenCols.map((g) => el("button", { class: "hidden-group", title: "Mostrar", onclick: () => { v.hiddenGroups = (v.hiddenGroups || []).filter((x) => x !== (g.value ?? "")); saveSchema(); } },
          P.chip(g.value ?? `Sem ${gp.name}`, g.color), el("span", { class: "muted" }, g.count)))) : null);
  }

  function column(v, gp, g, items) {
    const shown = shownProps(v, "board");
    const col = el("div", { class: "board-col", dataset: { value: g.value ?? "" } },
      el("div", { class: "board-col-head" },
        P.chip(g.value ?? `Sem ${gp.name}`, g.color), el("span", { class: "count" }, items.length),
        el("span", { class: "grow" }),
        ctx.canEdit ? el("button", { class: "icon-btn tiny", title: "Opções do grupo", onclick: (e) => menu(e.currentTarget, [
          { label: "Ocultar grupo", icon: "◌", run: () => { v.hiddenGroups = [...(v.hiddenGroups || []), g.value ?? ""]; saveSchema(); } },
          g.value !== null && { label: "Cor", icon: "●", run: () => colorMenu(e.target, gp, g.value) },
        ]) }, "⋯") : null,
        ctx.canEdit ? el("button", { class: "icon-btn tiny", title: "Nova neste grupo", onclick: () => { inlineGroup = g.value; renderBodyOnly(); } }, "+") : null),
      el("div", { class: "board-cards" }, items.map((r) => card(r, shown, gp))),
      ctx.canEdit ? (inlineGroup === g.value ? inlineCreate(v, { [gp.id]: g.value }) : el("button", { class: "board-add", onclick: () => { inlineGroup = g.value; renderBodyOnly(); } }, "+ Nova")) : null);
    if (ctx.canEdit) dropTarget(col, gp, g.value);
    return col;
  }

  function colorMenu(anchor, p, name) {
    menu(anchor, COLORS.map((c) => ({ label: c, icon: "●", run: () => {
      const o = (p.options || []).find((x) => x.name === name);
      if (o) { o.color = c; saveSchema(); }
    } })));
  }

  function inlineCreate(v, initial) {
    const input = el("input", { class: "inline-new", placeholder: "Digite um nome e tecle Enter" });
    let created = 0;
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const t = input.value.trim();
        if (!t) { inlineGroup = undefined; renderBodyOnly(); return; }
        newRow(defaultsFor(v, initial), t);
        created++;
        input.value = "";
        requestAnimationFrame(() => root.querySelector(".inline-new")?.focus());
      }
      if (e.key === "Escape") { inlineGroup = undefined; renderBodyOnly(); }
    });
    input.addEventListener("blur", () => setTimeout(() => {
      if (document.activeElement?.classList.contains("inline-new")) return;
      const t = input.value.trim();
      if (t) newRow(defaultsFor(v, initial), t);
      if (inlineGroup !== undefined) { inlineGroup = undefined; schedule(); }
    }, 120));
    requestAnimationFrame(() => input.focus());
    return el("div", { class: "card new" }, input, el("div", { class: "muted small" }, created ? "Criada. Digite a próxima ou Esc." : "Enter cria · Esc cancela"));
  }

  function card(row, shown, gp) {
    const pc = propCtx();
    const c = el("div", {
      class: "card",
      draggable: ctx.canEdit ? "true" : null,
      dataset: { id: row.id },
      onclick: (e) => { if (!e.target.closest(".rel-chip,a")) ctx.openRow(row.id); },
    },
    el("div", { class: "card-title" }, row.icon ? el("span", { class: "card-icon" }, iconNode(row.icon)) : null, el("span", {}, titleOf(row))),
    el("div", { class: "card-props" }, shown.map((p) => {
      const d = P.display(p, row, pc, { compact: true });
      return d ? el("div", { class: "card-prop", title: p.name }, d) : null;
    })));
    if (ctx.canEdit) {
      c.addEventListener("dragstart", (e) => {
        e.dataTransfer.setData("text/ordo-row", row.id);
        e.dataTransfer.effectAllowed = "move";
        requestAnimationFrame(() => c.classList.add("dragging"));
      });
      c.addEventListener("dragend", () => c.classList.remove("dragging"));
      c.addEventListener("contextmenu", (e) => { e.preventDefault(); rowMenu({ x: e.clientX, y: e.clientY }, row); });
    }
    return c;
  }

  function dropTarget(col, gp, value) {
    const cardsBox = () => col.querySelector(".board-cards");
    const marker = el("div", { class: "drop-marker" });
    const placeAt = (e) => {
      const cards = [...cardsBox().querySelectorAll(".card:not(.dragging)")];
      const after = cards.find((c) => e.clientY < c.getBoundingClientRect().top + c.offsetHeight / 2);
      return { cards, after };
    };
    col.addEventListener("dragover", (e) => {
      if (!e.dataTransfer.types.includes("text/ordo-row")) return;
      e.preventDefault();
      col.classList.add("over");
      const { after } = placeAt(e);
      if (after) cardsBox().insertBefore(marker, after); else cardsBox().append(marker);
    });
    col.addEventListener("dragleave", (e) => {
      if (!col.contains(e.relatedTarget)) { col.classList.remove("over"); marker.remove(); }
    });
    col.addEventListener("drop", (e) => {
      e.preventDefault();
      col.classList.remove("over");
      marker.remove();
      const id = e.dataTransfer.getData("text/ordo-row");
      const row = (data.rows || []).find((r) => r.id === id);
      if (!row) return;
      const { cards, after } = placeAt(e);
      const pos = (cid) => (data.rows || []).find((r) => r.id === cid)?.position ?? 0;
      let position;
      const ids = cards.map((c) => c.dataset.id).filter((x) => x !== id);
      const idx = after ? ids.indexOf(after.dataset.id) : ids.length;
      const prev = idx > 0 ? pos(ids[idx - 1]) : null;
      const next = idx < ids.length ? pos(ids[idx]) : null;
      if (prev !== null && next !== null) position = (prev + next) / 2;
      else if (prev !== null) position = prev + 1000;
      else if (next !== null) position = next - 1000;
      else position = row.position;
      const changed = (row.props[gp.id] ?? null) !== value;
      const body = { position };
      if (changed) body.props = { [gp.id]: value };
      store.setRowLocal(dbId, id, { position, ...(changed ? { props: { [gp.id]: value } } : {}) });
      store.patch(id, body).catch(() => refetch());
    });
  }

  function rowMenu(anchor, row) {
    menu(anchor, [
      { label: "Abrir", icon: "↗", run: () => ctx.openRow(row.id) },
      { label: "Abrir em página inteira", icon: "⤢", run: () => ctx.openRow(row.id, { full: true }) },
      { label: "Duplicar", icon: "⧉", run: () => newRow({ ...row.props }, (row.title || "") + " (cópia)") },
      "-",
      { label: "Excluir", icon: "🗑", danger: true, run: () => { store.archive(row.id).catch(() => {}); toast("Movida para a lixeira."); } },
    ]);
  }

  // ---------- Tabela ----------

  function table(v, rows) {
    const shown = shownProps(v, "table");
    const pc = propCtx();
    const titleProp = { id: "title", name: "Nome", type: "text" };
    const head = el("div", { class: "trow thead" },
      el("div", { class: "tcell tcell-title" }, headCell(v, titleProp)),
      shown.map((p) => el("div", { class: "tcell", style: { width: colWidth(p) + "px" } }, headCell(v, p))),
      ctx.canEdit ? el("div", { class: "tcell tcell-add" }, el("button", { class: "icon-btn tiny", title: "Nova propriedade", onclick: () => ctx.openSettings("props") }, "+")) : null);
    const bodyRows = rows.map((r) => el("div", { class: "trow", dataset: { id: r.id }, oncontextmenu: (e) => { if (ctx.canEdit) { e.preventDefault(); rowMenu({ x: e.clientX, y: e.clientY }, r); } } },
      titleCell(r),
      shown.map((p) => {
        const cell = el("div", { class: "tcell" + (ctx.canEdit && !P.COMPUTED.includes(p.type) ? " editable" : ""), style: { width: colWidth(p) + "px" } }, P.display(p, r, pc, { compact: false }) || "");
        if (ctx.canEdit && !P.COMPUTED.includes(p.type)) {
          cell.addEventListener("click", (e) => {
            if (e.target.closest("a,.rel-chip")) return;
            P.edit(cell, p, r, pc, (val) => setProp(r, p, val));
          });
        }
        return cell;
      })));
    const foot = ctx.canEdit
      ? (inlineGroup === "table" ? el("div", { class: "trow tnew" }, inlineCreate(v, {})) : el("button", { class: "board-add", onclick: () => { inlineGroup = "table"; renderBodyOnly(); } }, "+ Nova"))
      : null;
    return el("div", { class: "db-view table-scroll" }, el("div", { class: "dtable" }, head, bodyRows), foot,
      rows.length ? null : el("p", { class: "muted small pad" }, query || normFilters(v).length ? "Nada corresponde aos filtros." : "Nenhuma linha ainda."));
  }

  const colWidth = (p) => (["text", "relation", "multi_select", "lookup"].includes(p.type) ? 220 : ["checkbox", "id", "number", "rollup"].includes(p.type) ? 110 : 160);

  function headCell(v, p) {
    return el("button", { class: "thead-btn", onclick: (e) => menu(e.currentTarget, [
      { label: "Ordenar crescente", icon: "↑", run: () => { v.sort = [{ prop: p.id, dir: "asc" }]; saveSchema(); } },
      { label: "Ordenar decrescente", icon: "↓", run: () => { v.sort = [{ prop: p.id, dir: "desc" }]; saveSchema(); } },
      { label: "Filtrar", icon: "⚲", run: () => { v.filter = [...normFilters(v), { prop: p.id, op: ["select", "status", "multi_select", "person", "checkbox"].includes(p.type) ? "is" : "contains", value: p.type === "checkbox" ? true : "" }]; render(); requestAnimationFrame(() => { const links = root.querySelectorAll(".filter-chip .link"); editFilter(links[links.length - 1], v, normFilters(v).length - 1); }); } },
      p.id !== "title" && { label: "Ocultar nesta visão", icon: "◌", run: () => { v.visible = shownProps(v, "table").filter((x) => x.id !== p.id).map((x) => x.id); saveSchema(); } },
      p.id !== "title" && ctx.canEdit && { label: "Editar propriedade", icon: "⚙", run: () => ctx.openSettings("props", p.id) },
    ]) }, el("span", { class: "prop-icon" }, p.id === "title" ? "Aa" : P.typeIcon(p.type)), p.name);
  }

  function titleCell(r) {
    const cell = el("div", { class: "tcell tcell-title" });
    const text = el("span", { class: "tcell-text" }, r.icon ? [iconNode(r.icon), " "] : "", titleOf(r));
    const open = el("button", { class: "open-btn", onclick: (e) => { e.stopPropagation(); ctx.openRow(r.id); } }, "↗ Abrir");
    cell.append(text, open);
    cell.addEventListener("click", (e) => {
      if (e.target === open) return;
      if (!ctx.canEdit) return ctx.openRow(r.id);
      const input = el("input", { class: "tcell-input", value: r.title || "" });
      let done = false;
      const commit = (save) => {
        if (done) return;
        done = true;
        if (save && input.value !== (r.title || "")) setProp(r, { id: "title" }, input.value);
        schedule();
      };
      input.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") commit(true);
        if (ev.key === "Escape") commit(false);
      });
      input.addEventListener("blur", () => commit(true));
      cell.replaceChildren(input);
      input.focus();
    });
    return cell;
  }

  // ---------- Lista ----------

  function list(v, rows) {
    const shown = shownProps(v, "list");
    const pc = propCtx();
    return el("div", { class: "db-view dlist" },
      rows.map((r) => el("div", { class: "dlist-row", onclick: (e) => { if (!e.target.closest(".rel-chip,a")) ctx.openRow(r.id); } },
        el("span", { class: "dlist-title" }, el("span", { class: "card-icon" }, iconNode(r.icon)), titleOf(r)),
        el("span", { class: "dlist-props" }, shown.map((p) => P.display(p, r, pc, { compact: true }))))),
      ctx.canEdit ? (inlineGroup === "list" ? inlineCreate(v, {}) : el("button", { class: "board-add", onclick: () => { inlineGroup = "list"; renderBodyOnly(); } }, "+ Nova")) : null);
  }

  // ---------- Calendário ----------

  let month = null;
  function calendar(v, rows) {
    const dp = prop(v.dateProp) || props().find((p) => p.type === "date");
    if (!dp) {
      return el("div", { class: "db-view db-empty" }, el("p", {}, "Crie uma propriedade de data para usar o calendário."),
        ctx.canEdit ? el("button", { onclick: () => ctx.openSettings("props") }, "Adicionar propriedade") : null);
    }
    const today = new Date();
    if (!month) month = new Date(today.getFullYear(), today.getMonth(), 1);
    const first = new Date(month);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7)); // semana começa na segunda
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const byDay = new Map();
    for (const r of rows) {
      const d = r.props[dp.id];
      if (!d) continue;
      const k = String(d).slice(0, 10);
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k).push(r);
    }
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const k = iso(d);
      const cell = el("div", { class: "cal-day" + (d.getMonth() !== first.getMonth() ? " other" : "") + (k === iso(today) ? " today" : ""), dataset: { day: k } },
        el("div", { class: "cal-num" }, d.getDate(),
          ctx.canEdit ? el("button", { class: "icon-btn tiny cal-add", title: "Nova neste dia", onclick: () => newRow(defaultsFor(v, { [dp.id]: k }), "", { open: true }) }, "+") : null),
        (byDay.get(k) || []).map((r) => {
          const chipEl = el("div", { class: "cal-item", draggable: ctx.canEdit ? "true" : null, onclick: () => ctx.openRow(r.id) }, r.icon ? [iconNode(r.icon), " "] : "", titleOf(r));
          chipEl.addEventListener("dragstart", (e) => e.dataTransfer.setData("text/ordo-row", r.id));
          return chipEl;
        }));
      if (ctx.canEdit) {
        cell.addEventListener("dragover", (e) => { e.preventDefault(); cell.classList.add("over"); });
        cell.addEventListener("dragleave", () => cell.classList.remove("over"));
        cell.addEventListener("drop", (e) => {
          e.preventDefault();
          cell.classList.remove("over");
          const id = e.dataTransfer.getData("text/ordo-row");
          const row = (data.rows || []).find((r) => r.id === id);
          if (row) setProp(row, dp, k);
        });
      }
      days.push(cell);
    }
    const label = first.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    const undated = rows.filter((r) => !r.props[dp.id]).length;
    return el("div", { class: "db-view calendar" },
      el("div", { class: "cal-head" },
        el("strong", { class: "cal-month" }, label[0].toUpperCase() + label.slice(1)),
        el("span", { class: "muted small" }, `por ${dp.name}${undated ? ` · ${undated} sem data` : ""}`),
        el("span", { class: "grow" }),
        el("button", { class: "icon-btn", onclick: () => { month = new Date(first.getFullYear(), first.getMonth() - 1, 1); renderBodyOnly(); } }, "‹"),
        el("button", { class: "small", onclick: () => { month = null; renderBodyOnly(); } }, "Hoje"),
        el("button", { class: "icon-btn", onclick: () => { month = new Date(first.getFullYear(), first.getMonth() + 1, 1); renderBodyOnly(); } }, "›")),
      el("div", { class: "cal-grid" }, ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"].map((d) => el("div", { class: "cal-wd" }, d)), days));
  }

  // ---------- Início ----------

  async function boot() {
    if (data?.rows) render();
    else root.replaceChildren(el("div", { class: "db-loading" }, el("span", { class: "spinner" }), " Carregando…"));
    try {
      const fresh = await store.load(dbId, { fresh: !!data?.rows });
      if (destroyed) return;
      data = fresh;
      render();
    } catch (err) {
      if (!data?.rows) root.replaceChildren(el("p", { class: "error" }, err.message));
    }
  }
  boot();

  return {
    refresh: () => { data = store.cached(dbId) || data; schedule(); },
    reload: refetch,
    destroy: () => {
      destroyed = true;
      for (const off of offs) off();
    },
  };
}
